/** Field names aligned with backend session + GET /users/me user slice. */
const ACADEMIC_KEYS = /** @type {const} */ ([
  'rollNumber',
  'joinYear',
  'currentYear',
  'branch',
  'entryType',
  'college',
]);

/**
 * Reads academic fields from `res.academicProfile` when present, else from top-level `res`.
 * Does not assume any key exists on either object.
 *
 * @param {Record<string, unknown> | null | undefined} res
 * @returns {Record<(typeof ACADEMIC_KEYS)[number], unknown>}
 */
function academicProfileSource(res) {
  /** @type {Record<string, unknown>} */
  const src = {};
  if (!res || typeof res !== 'object') return src;

  const nested =
    res.academicProfile != null && typeof res.academicProfile === 'object'
      ? /** @type {Record<string, unknown>} */ (res.academicProfile)
      : null;

  for (const key of ACADEMIC_KEYS) {
    const fromNested =
      nested && Object.prototype.hasOwnProperty.call(nested, key) ? nested[key] : undefined;
    const fromTop = Object.prototype.hasOwnProperty.call(res, key) ? res[key] : undefined;
    src[key] = fromNested !== undefined ? fromNested : fromTop;
  }
  return src;
}

/**
 * Maps login or /users/me style payloads into a fixed academic profile shape.
 * Uses `|| null` so missing/falsy scalars become `null` (per product convention).
 *
 * @param {Record<string, unknown> | null | undefined} res
 * @param {string} [context] - e.g. `'login'`, `'GET /users/me'` (for warnings)
 * @returns {{
 *   rollNumber: string | null,
 *   joinYear: number | null,
 *   currentYear: number | null,
 *   branch: string | null,
 *   entryType: string | null,
 *   college: string | null
 * }}
 */
export function extractAcademicProfileFromApiResponse(res, context = 'api') {
  if (res == null || typeof res !== 'object') {
    const empty = {
      rollNumber: null,
      joinYear: null,
      currentYear: null,
      branch: null,
      entryType: null,
      college: null,
    };
    if (process.env.NODE_ENV !== 'production') {
      console.warn(`[StackTrack] academicProfile: missing or non-object response (${context})`, res);
    }
    return empty;
  }

  const src = academicProfileSource(/** @type {Record<string, unknown>} */ (res));

  const academicProfile = {
    rollNumber: src.rollNumber || null,
    joinYear: src.joinYear || null,
    currentYear: src.currentYear || null,
    branch: src.branch || null,
    entryType: src.entryType || null,
    college: src.college || null,
  };

  const missing = ACADEMIC_KEYS.filter((k) => academicProfile[k] == null);
  if (missing.length > 0 && process.env.NODE_ENV !== 'production') {
    console.warn(`[StackTrack] academicProfile: null or missing fields (${context}):`, missing);
  }

  return academicProfile;
}

/**
 * Removes academic fields and nested `academicProfile` so `user` can be stored separately.
 *
 * @param {Record<string, unknown> | null | undefined} flat
 * @returns {Record<string, unknown> | null | undefined}
 */
export function stripAcademicFieldsFromUserPayload(flat) {
  if (flat == null || typeof flat !== 'object') return flat;
  const next = { ...flat };
  delete next.academicProfile;
  for (const key of ACADEMIC_KEYS) {
    delete next[key];
  }
  return next;
}

/**
 * Safe string for UI when backend / session has not populated a field yet.
 *
 * @param {unknown} value
 * @param {string} [fallback='—']
 * @returns {string}
 */
export function displayAcademicField(value, fallback = '—') {
  if (value == null) return fallback;
  if (typeof value === 'number') {
    return Number.isFinite(value) ? String(value) : fallback;
  }
  const s = String(value).trim();
  return s === '' ? fallback : s;
}

/**
 * Dashboard chip: branch (or "Not available") and optional year — never assumes `academicProfile` exists.
 *
 * @param {Record<string, unknown> | null | undefined} academicProfile
 * @returns {string}
 */
export function formatAcademicDashboardChipLabel(academicProfile) {
  const branch = displayAcademicField(academicProfile?.branch, 'Not available');
  const cy = academicProfile?.currentYear;
  const hasYear =
    cy != null &&
    cy !== '' &&
    (typeof cy === 'number' ? Number.isFinite(cy) : String(cy).trim() !== '');
  if (!hasYear) return branch;
  const yearStr = typeof cy === 'number' ? String(cy) : String(cy).trim();
  return `${branch} • ${yearStr} Year`;
}
